import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { logError } from "@/lib/logger";
import { toast } from "sonner";
import {
    Gift,
    Copy,
    Check,
    WhatsappLogo,
    LinkedinLogo,
    XLogo,
    InstagramLogo,
    EnvelopeSimple,
    Users,
    Trophy,
    Wallet,
    ChatCircleDots,
    Download,
} from "@phosphor-icons/react";
import ReferralPoster from "@/components/ReferralPoster";

const CURRENCY_SYMBOL = { INR: "₹", USD: "$", EUR: "€", GBP: "£" };

export default function CandidateReferrals() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);
    const [posterOpen, setPosterOpen] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        api
            .get("/me/candidate/referrals")
            .then((r) => setData(r.data))
            .catch((e) => logError("Failed to load referrals:", e))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading) return <div className="p-10 text-[#525252]">Loading…</div>;
    if (!data) return <div className="p-10 text-[#525252]">Couldn&apos;t load referrals.</div>;

    const code = data.referral_code;
    // Route shares through /api/r/{code} so social crawlers get a rich
    // preview card (referrer name + bounty). Real browsers 302 to signup.
    const shareUrl = `${window.location.origin}/api/r/${code}`;
    const withChannel = (ch) => `${shareUrl}?src=${ch}`;
    const symbol = CURRENCY_SYMBOL[data.bounty_currency] || data.bounty_currency + " ";
    const message = (ch) =>
        `I use NaukriDaddy to track roles at top Indian agencies — sign up with my code ${code} and if I refer someone who gets hired I earn a bounty. Join here: ${withChannel(ch)}`;

    // Preview iframes silently drop target=_blank; navigate() escapes via
    // window.top before falling back to same-tab nav so shares always "go".
    //
    // NOTE: window.open(url, "_blank", "noopener") *always* returns null in
    // modern browsers because noopener detaches the reference. Never treat
    // null as "popup blocked" outside an iframe or we'd wrongly navigate
    // the current tab on top of the new one.
    const navigate = (url) => {
        const inIframe = (() => {
            try { return window.top !== window.self; } catch (_) { return true; }
        })();
        try {
            const w = window.open(url, "_blank", "noopener,noreferrer");
            if (!inIframe) return;
            if (w) return;
        } catch (_) { /* fall through */ }
        try {
            window.top.location.href = url;
        } catch (_) {
            window.location.href = url;
        }
    };

    // Instagram has no public share intent — copy the full caption and open
    // Instagram (deep-link on mobile, web on desktop) so the user can paste
    // it into a Story or DM. Same behavior as ShareJobModal + ReferAndEarn.
    const shareInstagram = async () => {
        const caption = message("instagram");
        let ok = false;
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(caption);
                ok = true;
            }
        } catch (_) { /* fall through */ }
        if (!ok) {
            try {
                const ta = document.createElement("textarea");
                ta.value = caption;
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.focus();
                ta.select();
                ok = document.execCommand("copy");
                document.body.removeChild(ta);
            } catch (_) { /* ignore */ }
        }
        toast.success(
            ok
                ? "Caption copied. Open Instagram and paste it into your Story, Bio, or DM."
                : "Long-press the link below to copy manually, then open Instagram.",
            { duration: 5500 },
        );
    };

    const copy = async () => {
        let ok = false;
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(withChannel("copy"));
                ok = true;
            }
        } catch (_) { /* fall through */ }
        if (!ok) {
            try {
                const ta = document.createElement("textarea");
                ta.value = withChannel("copy");
                ta.style.position = "fixed";
                ta.style.opacity = "0";
                document.body.appendChild(ta);
                ta.focus();
                ta.select();
                ok = document.execCommand("copy");
                document.body.removeChild(ta);
            } catch (_) { /* ignore */ }
        }
        if (ok) {
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        }
    };

    return (
        <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-[1100px] mx-auto" data-testid="candidate-referrals-page">
            <div className="mb-8">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                    Earn a bounty
                </p>
                <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-ink">
                    Refer &amp; earn.
                </h1>
                <p className="text-[#525252] mt-3 max-w-xl">
                    Share your code with friends. When they sign up and get hired through NaukriDaddy,
                    you earn <span className="font-bold text-ink" data-testid="ref-bounty-amount">{symbol}{data.bounty_amount.toLocaleString("en-IN")}</span>.
                </p>
            </div>

            {/* Referral code card */}
            <div className="bg-ink text-white p-6 sm:p-8 mb-8 flex flex-col sm:flex-row items-start sm:items-center gap-6" data-testid="ref-code-card">
                <div className="w-14 h-14 bg-gradient-to-br from-splash to-cobalt flex items-center justify-center shrink-0">
                    <Gift size={26} weight="bold" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-white/60 mb-2">
                        Your referral code
                    </p>
                    <p
                        data-testid="ref-code"
                        className="font-heading font-black text-4xl sm:text-5xl tracking-tighter text-splash select-all"
                    >
                        {code}
                    </p>
                </div>
                <div className="flex flex-col gap-2 sm:min-w-[220px]">
                    <button
                        onClick={copy}
                        data-testid="ref-copy-link"
                        className={`px-4 py-3 text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 transition-colors ${
                            copied
                                ? "bg-emerald-500 text-white"
                                : "bg-white text-ink hover:bg-splash hover:text-ink"
                        }`}
                    >
                        {copied ? <Check size={14} weight="bold" /> : <Copy size={14} weight="bold" />}
                        {copied ? "Link copied" : "Copy invite link"}
                    </button>
                    <div
                        data-testid="ref-share-buttons"
                        className="grid grid-cols-5 gap-1.5"
                    >
                        <a
                            data-testid="ref-share-whatsapp"
                            href={`https://wa.me/?text=${encodeURIComponent(message("whatsapp"))}`}
                            target="_blank"
                            rel="noreferrer"
                            title="WhatsApp"
                            aria-label="Share on WhatsApp"
                            className="aspect-square bg-[#25D366] hover:bg-[#1ebe5a] flex items-center justify-center transition-colors"
                            onClick={(e) => {
                                e.preventDefault();
                                navigate(`https://wa.me/?text=${encodeURIComponent(message("whatsapp"))}`);
                            }}
                        >
                            <WhatsappLogo size={16} weight="bold" />
                        </a>
                        <a
                            data-testid="ref-share-linkedin"
                            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(withChannel("linkedin"))}`}
                            target="_blank"
                            rel="noreferrer"
                            title="LinkedIn"
                            aria-label="Share on LinkedIn"
                            className="aspect-square bg-[#0A66C2] hover:bg-[#084b91] flex items-center justify-center transition-colors"
                            onClick={(e) => {
                                e.preventDefault();
                                navigate(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(withChannel("linkedin"))}`);
                            }}
                        >
                            <LinkedinLogo size={16} weight="bold" />
                        </a>
                        <a
                            data-testid="ref-share-twitter"
                            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(message("twitter"))}`}
                            target="_blank"
                            rel="noreferrer"
                            title="X (Twitter)"
                            aria-label="Share on X"
                            className="aspect-square bg-black hover:bg-[#222] flex items-center justify-center transition-colors"
                            onClick={(e) => {
                                e.preventDefault();
                                navigate(`https://twitter.com/intent/tweet?text=${encodeURIComponent(message("twitter"))}`);
                            }}
                        >
                            <XLogo size={16} weight="bold" />
                        </a>
                        <button
                            type="button"
                            data-testid="ref-share-instagram"
                            onClick={shareInstagram}
                            title="Instagram"
                            aria-label="Share on Instagram"
                            className="aspect-square bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] hover:opacity-90 flex items-center justify-center transition-opacity"
                        >
                            <InstagramLogo size={16} weight="bold" />
                        </button>
                        <a
                            data-testid="ref-share-email"
                            href={`mailto:?subject=${encodeURIComponent("Join NaukriDaddy with my code")}&body=${encodeURIComponent(message("email"))}`}
                            title="Email"
                            aria-label="Share via email"
                            className="aspect-square bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                        >
                            <EnvelopeSimple size={16} weight="bold" />
                        </a>
                    </div>
                    <button
                        type="button"
                        onClick={() => setPosterOpen(true)}
                        data-testid="refer-download-poster"
                        className="mt-5 w-full border border-white/20 hover:border-white hover:bg-white/10 text-white px-4 py-2.5 text-[11px] font-black uppercase tracking-[0.2em] inline-flex items-center justify-center gap-2 transition-colors"
                    >
                        <Download size={12} weight="bold" /> Download poster (Instagram/WhatsApp)
                    </button>
                </div>
            </div>
            <ReferralPoster
                open={posterOpen}
                onClose={() => setPosterOpen(false)}
                referralCode={code}
                referrerName={data.referrer_name}
                bountyLabel={`${symbol}${data.bounty_amount.toLocaleString("en-IN")}`}
                shareUrl={shareUrl}
            />

            {/* Stats grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8">
                <Stat
                    icon={Users}
                    label="Signups"
                    value={data.counts.signups}
                    testid="ref-stat-signups"
                    accent="text-cobalt"
                />
                <Stat
                    icon={ChatCircleDots}
                    label="Interviewing"
                    value={data.counts.interviewing}
                    testid="ref-stat-interviewing"
                    accent="text-[#F59E0B]"
                />
                <Stat
                    icon={Trophy}
                    label="Hired"
                    value={data.counts.hired}
                    testid="ref-stat-hired"
                    accent="text-[#059669]"
                />
                <Stat
                    icon={Wallet}
                    label={`Earned (${data.earnings.currency})`}
                    value={`${symbol}${(data.earnings.paid + data.earnings.pending).toLocaleString("en-IN")}`}
                    subline={data.earnings.pending > 0 ? `${symbol}${data.earnings.pending.toLocaleString("en-IN")} pending` : "all cleared"}
                    testid="ref-stat-earned"
                    accent="text-ink"
                    monospace
                />
            </div>

            {/* Channel breakdown */}
            {data.channels && data.channels.length > 0 && (
                <div className="mb-8" data-testid="ref-channels">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#A3A3A3] mb-2">
                        Which channel wins?
                    </p>
                    <div className="bg-white border border-[#E5E5E5] divide-y divide-[#F5F5F5]">
                        {data.channels.map((row) => {
                            const total = data.channels.reduce((s, r) => s + r.signups, 0) || 1;
                            const pct = Math.round((row.signups / total) * 100);
                            return (
                                <div key={row.channel} className="px-4 py-3" data-testid={`ref-channel-${row.channel}`}>
                                    <div className="flex items-center justify-between text-xs mb-1.5">
                                        <span className="font-bold text-ink uppercase tracking-wider">{row.channel}</span>
                                        <span className="text-[#525252]">
                                            <span className="font-bold text-ink">{row.signups}</span> signup{row.signups !== 1 ? "s" : ""}
                                            {row.hires > 0 && (
                                                <> · <span className="font-bold text-emerald-600">{row.hires}</span> hired</>
                                            )}
                                        </span>
                                    </div>
                                    <div className="h-1.5 bg-[#F5F5F5] overflow-hidden">
                                        <div
                                            className="h-full bg-gradient-to-r from-splash to-cobalt"
                                            style={{ width: `${pct}%` }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Referred people */}
            {data.referred.length > 0 && (
                <div className="mb-8" data-testid="ref-referred-list">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#A3A3A3] mb-2">
                        People you referred
                    </p>
                    <div className="bg-white border border-[#E5E5E5] divide-y divide-[#F5F5F5]">
                        {data.referred.map((r) => (
                            <div key={r.id} className="px-4 py-3 flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="text-sm font-bold text-ink truncate">{r.name}</p>
                                    <p className="text-xs text-[#525252] truncate">{r.email}</p>
                                </div>
                                <p className="text-[11px] text-[#A3A3A3] shrink-0">
                                    {new Date(r.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Payout history */}
            {data.payouts.length > 0 && (
                <div data-testid="ref-payouts-list">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#A3A3A3] mb-2">
                        Payout history
                    </p>
                    <div className="bg-white border border-[#E5E5E5] divide-y divide-[#F5F5F5]">
                        {data.payouts.map((p) => (
                            <div key={p.id} className="px-4 py-3 flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="text-sm font-bold text-ink">
                                        {symbol}{p.amount.toLocaleString("en-IN")}
                                    </p>
                                    <p className="text-[11px] text-[#525252]">
                                        Created {new Date(p.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                                        {p.paid_at && ` · Paid ${new Date(p.paid_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`}
                                    </p>
                                </div>
                                <span
                                    className={`text-[10px] font-black uppercase tracking-wider px-2 py-1 shrink-0 ${
                                        p.status === "paid"
                                            ? "bg-[#ECFDF5] text-[#059669]"
                                            : "bg-[#FEF3C7] text-[#B45309]"
                                    }`}
                                >
                                    {p.status}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {data.referred.length === 0 && (
                <div className="bg-white border border-[#E5E5E5] p-8 text-center">
                    <p className="text-sm text-[#525252]">
                        No signups yet — share your code with 3-4 friends to get started.
                    </p>
                </div>
            )}
        </div>
    );
}

function Stat({ icon: Icon, label, value, subline, testid, accent, monospace }) {
    return (
        <div className="bg-white border border-[#E5E5E5] p-4 sm:p-5" data-testid={testid}>
            <div className="flex items-center gap-2 mb-2">
                <Icon size={14} weight="bold" className={accent} />
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#525252]">
                    {label}
                </p>
            </div>
            <p className={`font-heading font-black text-2xl sm:text-3xl tracking-tighter ${accent} ${monospace ? "font-mono" : ""}`}>
                {value}
            </p>
            {subline && (
                <p className="text-[11px] text-[#A3A3A3] mt-1">{subline}</p>
            )}
        </div>
    );
}
